import React, { useEffect, useState } from 'react';
import { convertExcelDate, fetchPresignedFile } from '../../../utils/fileUtils';
import { Box } from '@mui/material';
import ExcelJS from 'exceljs';

interface ExcelViewerProps {
  previewFile: string;
}

const ExcelViewer: React.FC<ExcelViewerProps> = ({ previewFile }) => {
  const [excelContent, setExcelContent] = useState<React.ReactNode | null>(null);

  useEffect(() => {
    const fetchExcelContent = async () => {
      try {
        const response = await fetchPresignedFile(previewFile);
        if (!response.ok) {
          throw new Error(`Failed to fetch Excel file: ${response.statusText}`);
        }

        const arrayBuffer = await response.arrayBuffer();

        const workbook = new ExcelJS.Workbook();
        await workbook.xlsx.load(arrayBuffer);

        const worksheet = workbook.worksheets[0];

        if (!worksheet) {
          setExcelContent(<div>No worksheet found.</div>);
          return;
        }

        const rows: (string | number | null)[][] = [];

        worksheet.eachRow({ includeEmpty: true }, (row) => {
          const rowValues = row.values as Array<string | number | Date | null | undefined>;

          const normalizedRow = rowValues
            .slice(1)
            .map((cell) => {
              if (cell instanceof Date) {
                return cell.toLocaleDateString();
              }

              if (typeof cell === 'number' && cell > 59 && cell < 2958465) {
                return convertExcelDate(cell).toLocaleDateString();
              }

              if (cell === undefined) {
                return null;
              }

              return cell as string | number | null;
            });

          rows.push(normalizedRow);
        });

        const tableElement = (
          <table style={{ borderCollapse: 'collapse', width: '100%' }}>
            <tbody>
              {rows.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  {row.map((cell, cellIndex) => (
                    <td
                      key={cellIndex}
                      style={{
                        border: '1px solid #ddd',
                        padding: '8px',
                        textAlign: 'left',
                        backgroundColor: rowIndex === 0 ? '#f2f2f2' : 'transparent',
                      }}
                    >
                      {cell !== null && cell !== undefined ? String(cell) : ''}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        );

        setExcelContent(tableElement);
      } catch (error) {
        console.error('Error converting Excel:', error);
      }
    };

    fetchExcelContent();
  }, [previewFile]);

  const excelStyle = {
    backgroundColor: '#fff',
    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
    borderRadius: '8px',
    padding: '16px',
    margin: '16px auto',
    width: '100%',
    maxWidth: '1200px',
    overflowX: 'auto',
  };

  return (
    <Box sx={{ ...excelStyle, position: 'relative', zIndex: 1, height: '100%', overflowY: 'auto' }}>
      {excelContent}
    </Box>
  );
};

export default ExcelViewer;